import { Icon, Badge } from "@elaniintc/goes-design-system";
import { SearchDropdown } from "@/components/ui/search-dropdown";
import { useDocumentGeneratorNode } from "../hooks";

interface TemplateSelectorsProps {
  error?: string | null;
  placeholder?: string;
  isLatestVersion?: boolean;
}

export const TemplateSelector = ({
  error = null,
  placeholder = "Seleccionar una plantilla",
  isLatestVersion,
}: TemplateSelectorsProps) => {
  const {
    selectedTemplate,
    setSelectedTemplate: onTemplateChange,
    templates,
    isLoadingTemplates: isLoading,
    onTemplateSearch,
    templateSearchQuery,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
  } = useDocumentGeneratorNode();

  const isEmpty = templates.length === 0 || error !== null;

  return (
    <SearchDropdown
      placeholder={placeholder}
      searchPlaceholder="Buscar plantilla..."
      widthClassName="w-full"
      contentWidthClassName="w-[var(--radix-dropdown-menu-trigger-width)] !min-w-0 !max-w-none"
      isLoading={isLoading}
      isFetching={isFetchingNextPage}
      hasNextPage={hasNextPage}
      fetchNextPage={fetchNextPage}
      isEmpty={isEmpty}
      selectedItem={selectedTemplate}
      data={templates}
      onSearch={onTemplateSearch}
      renderItem={(template, index, closeMenu) => {
        if (index === -1) {
          // Si hay error
          if (error) {
            return (
              <>
                <Icon
                  type="far"
                  name="triangle-exclamation"
                  className="flex-shrink-0 text-red-600"
                />
                <span className="flex-1 truncate text-left">
                  Error al cargar las plantillas
                </span>
              </>
            );
          }

          // Si no hay templates en general
          if (templates.length === 0 && !selectedTemplate) {
            return (
              <>
                <Icon
                  type="far"
                  name="file-lines"
                  className="text-body-quiet flex-shrink-0"
                />
                <span className="flex-1 truncate text-left text-body-quiet">
                  No hay plantillas disponibles
                </span>
              </>
            );
          }

          // Si no hay resultados de búsqueda
          if (templates.length === 0 && !selectedTemplate) {
            return (
              <>
                <Icon
                  type="far"
                  name="magnifying-glass"
                  className="text-body-quiet flex-shrink-0"
                />
                <span className="flex-1 truncate text-left text-body-quiet">
                  No se encontraron resultados para "{templateSearchQuery}"
                </span>
              </>
            );
          }

          // Item seleccionado (cuando hay template)
          return (
            <>
              <Icon
                type="far"
                name="file-lines"
                className="text-body flex-shrink-0"
              />
              <span className="flex-1 truncate text-left">
                {template?.name}
              </span>
              <Badge
                shape="pill"
                variant={isLatestVersion ? "neutral" : "warning"}
                className="flex-shrink-0"
              >
                {`v${template?.version}`}
              </Badge>
            </>
          );
        }

        return (
          !error && (
            <li
              key={template?.id}
              onClick={() => {
                onTemplateChange(template);
                closeMenu();
              }}
              className="cursor-pointer rounded-lg px-3 py-2 text-body hover:text-body-loud hover:bg-neutral-object-accent-quiet flex items-center gap-3"
            >
              <Icon
                type="far"
                name="file-lines"
                className="text-body flex-shrink-0"
              />
              <div className="flex flex-col flex-1 min-w-0">
                <span>{template?.name}</span>
              </div>
              <Badge
                shape="pill"
                variant="neutral"
                className="flex-shrink-0 ml-auto"
              >
                {`v${template?.version}`}
              </Badge>
            </li>
          )
        );
      }}
    />
  );
};
